import "@mdxeditor/editor/style.css";

import { useEffect } from "react";

import {
  BlockTypeSelect,
  BoldItalicUnderlineToggles,
  ListsToggle,
  MDXEditor,
  UndoRedo,
  headingsPlugin,
  listsPlugin,
  markdownShortcutPlugin,
  quotePlugin,
  thematicBreakPlugin,
  toolbarPlugin,
} from "@mdxeditor/editor";
import {
  Controller,
  FieldError,
  Control,
  FieldValues,
  RegisterOptions,
  FieldPath,
} from "react-hook-form";

type MarkdownFieldProps<T extends FieldValues> = {
  name: FieldPath<T>;
  label?: string;
  error?: FieldError;
  isDisabled?: boolean;
  control: Control<T>;
  rules?: RegisterOptions<T, FieldPath<T>>;
  htmlFor?: string;
  defaultValue?: string;
  placeholder?: string;
  editorKey?: string;
};

interface SharedMarkdownFieldProps<T extends FieldValues>
  extends Omit<MarkdownFieldProps<T>, "ref" | "onChange"> {
  control: Control<T>;
  name: FieldPath<T>;
  rules?: RegisterOptions<T, FieldPath<T>>;
  label?: string;
  id?: string;
  error?: FieldError;
  htmlFor?: string;
  defaultValue?: string;
  placeholder?: string;
  editorKey?: string;
}

export function MarkdownField<T extends FieldValues>({
  error,
  isDisabled = false,
  control,
  name,
  rules,
  label,
  htmlFor,
  defaultValue,
  placeholder,
  editorKey,
  id,
  ...rest
}: SharedMarkdownFieldProps<T>) {
  // Force z-index on Radix portals after component mounts
  useEffect(() => {
    const interval = setInterval(() => {
      const portals = document.querySelectorAll("[data-radix-select-content]");
      portals.forEach((portal) => {
        const element = portal as HTMLElement;
        element.style.zIndex = "999999";
      });
    }, 100);

    return () => clearInterval(interval);
  }, []);

  // Base key used to control MDXEditor remounting behavior
  const baseKey = editorKey || id || name;

  return (
    <div>
      {error ? (
        <div className="block font-normal tmb-1 text-red-400">
          <p>{error.message}</p>
        </div>
      ) : (
        <label
          htmlFor={htmlFor}
          className="block font-normal tmb-1 text-gray-500"
        >
          {label}
        </label>
      )}
      <Controller<T, FieldPath<T>>
        control={control}
        name={name}
        rules={rules}
        render={({ field: { onChange, value } }) => {
          return (
            <div className="my-2">
              <MDXEditor
                key={baseKey}
                markdown={value || ""}
                onChange={onChange}
                placeholder={placeholder}
                readOnly={isDisabled}
                className="border border-solid border-gray-300 rounded focus-within:border-blue-600 z-[1001]"
                contentEditableClassName="prose max-w-none min-h-[200px] p-4"
                plugins={[
                  headingsPlugin({ allowedHeadingLevels: [1, 2, 3, 4, 5, 6] }),
                  listsPlugin(),
                  quotePlugin(),
                  thematicBreakPlugin(),
                  markdownShortcutPlugin(),
                  toolbarPlugin({
                    toolbarContents: () => (
                      <>
                        <UndoRedo />
                        <BoldItalicUnderlineToggles />
                        <BlockTypeSelect />
                        <ListsToggle options={["bullet", "number"]} />
                      </>
                    ),
                  }),
                ]}
                {...rest}
              />
            </div>
          );
        }}
      />
    </div>
  );
}
